import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';

interface Notification {
  _id: string;
  title: string;
  message: string;
  type: 'general' | 'project' | 'payment' | 'milestone' | 'task' | 'message' | 'system';
  category: 'info' | 'success' | 'warning' | 'error';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  isRead: boolean;
  createdAt: string;
  actionUrl?: string;
  actionText?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Notification) => void;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  removeNotification: (notificationId: string) => void;
  fetchNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Get the correct API base URL based on environment
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};


const API_BASE_URL = getApiBaseUrl();

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const { user } = useAuth();

  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    const now = Date.now();
    if (now - lastFetchTime < 10000) return; // Throttle requests to once every 10 seconds
    setLastFetchTime(now);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      
      const res = await fetch(`${API_BASE_URL}/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!res.ok) {
        throw new Error('Failed to fetch notifications');
      }
      
      const data = await res.json();
      setNotifications(data.notifications || []);
    } catch (error) {
      // Don't show error toast for this as it's a background operation
    }
  }, [user, lastFetchTime]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      
      // Set up polling for new notifications
      const interval = setInterval(() => {
        fetchNotifications();
      }, 60000); // Check every minute
      
      return () => clearInterval(interval);
    } else {
      setNotifications([]);
    }
  }, [user, fetchNotifications]);

  useEffect(() => {
    const count = notifications.filter(n => !n.isRead).length;
    setUnreadCount(count);
  }, [notifications]);

  const addNotification = useCallback((notification: Notification) => {
    setNotifications(prev => [notification, ...prev.slice(0, 49)]);
    
    // Show toast notification based on category
    switch (notification.category) {
      case 'success':
        toast.success(notification.message);
        break;
      case 'warning':
        toast.warning(notification.message);
        break;
      case 'error':
        toast.error(notification.message);
        break;
      default:
        toast.info(notification.message);
    }
  }, []);

 const markAsRead = useCallback(async (notificationId: string) => {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}/read`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to mark notification as read');
    }

    // ✅ Refetch from backend after successful update
    await fetchNotifications();
  } catch (error) {
  }
}, [fetchNotifications]);


  const markAllAsRead = useCallback(async () => {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const response = await fetch(`${API_BASE_URL}/notifications/read-all`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to mark all as read');
    }

    // ✅ Refetch after update
    await fetchNotifications();
  } catch (error) {
  }
}, [fetchNotifications]);


  const removeNotification = useCallback((notificationId: string) => {
    setNotifications(prev => prev.filter(n => n._id !== notificationId));
  }, []);

  const value = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    fetchNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};